import { Checkbox } from "@package/subframe/components/Checkbox";

import type { NurseryRole } from "@/contexts/NurseryContext";
import { isCheckboxBubbleClick } from "@/lib/event-records/checkbox-bubble-click";
import {
  memberDisplayName,
  type NurseryMemberOption,
} from "@/lib/member-display";
import { nurseryRoleLabel } from "@/lib/roles";

export interface AcknowledgementRecipientPickerProps {
  members: (NurseryMemberOption & { role?: string })[];
  selectedUserIds: string[];
  onToggle: (userId: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
  /** People already on the record — shown ticked and locked rather than hidden. */
  alreadyRequestedUserIds?: string[];
}

/**
 * Picks the people to ask to acknowledge an event record (NAU-342 AC 6).
 *
 * A scrollable checkbox list rather than a popover multi-select: the same shape as
 * the checklist schedule's team-member picker, which is the established pattern for
 * choosing several nursery members, and it keeps every name visible while you scan
 * for the right one.
 *
 * Someone already asked stays listed, ticked and disabled: hiding them would make
 * it look as though they had never been asked.
 */
export function AcknowledgementRecipientPicker({
  members,
  selectedUserIds,
  onToggle,
  isLoading = false,
  disabled = false,
  alreadyRequestedUserIds = [],
}: AcknowledgementRecipientPickerProps) {
  const alreadyRequested = new Set(alreadyRequestedUserIds);

  if (isLoading) {
    return (
      <span className="text-body font-body text-subtext-color">
        Loading team members…
      </span>
    );
  }

  if (members.length === 0) {
    return (
      <span className="text-body font-body text-subtext-color">
        No team members found for this nursery.
      </span>
    );
  }

  return (
    <div className="flex max-h-[240px] w-full flex-col items-start gap-2 overflow-y-auto rounded-md border border-solid border-neutral-border bg-default-background p-3">
      {members.map((member) => {
        const isRequested = alreadyRequested.has(member.userId);
        const isSelected =
          isRequested || selectedUserIds.includes(member.userId);
        const roleLabel = member.role
          ? nurseryRoleLabel(member.role as NurseryRole)
          : "";

        const isDisabled = disabled || isRequested;

        return (
          // A div, not a button, and the Checkbox carries no handler of its own:
          // nesting an interactive control inside a <button> is invalid markup,
          // and both handlers would fire for one click.
          //
          // That alone does NOT stop the re-dispatch loop described on the click
          // handler below — the loop comes from the hidden input bubbling into
          // whatever ancestor holds the handler, so it survives any change of
          // element. The guard in onClick is what actually stops it.
          <div
            key={member.userId}
            role="button"
            tabIndex={isDisabled ? -1 : 0}
            aria-pressed={isSelected}
            aria-disabled={isDisabled}
            className={`flex w-full items-center gap-3 rounded-sm px-1 py-1 text-left ${
              isDisabled
                ? "cursor-not-allowed opacity-60"
                : "cursor-pointer hover:bg-neutral-50"
            }`}
            onClick={(event) => {
              if (isDisabled) return;
              // Drop the synthetic click Radix's hidden input re-dispatches on
              // every `checked` change. Without this the row toggles itself
              // forever on any screen where the picker sits inside a <form> —
              // reporting an event record does, the request dialog does not,
              // which is why it worked in one place and locked up in the other.
              if (isCheckboxBubbleClick(event.target as HTMLInputElement)) {
                return;
              }
              onToggle(member.userId);
            }}
            onKeyDown={(event) => {
              if (isDisabled) return;
              if (event.key !== "Enter" && event.key !== " ") return;
              event.preventDefault();
              onToggle(member.userId);
            }}
            data-testid={`ack-recipient-${member.userId}`}
          >
            <Checkbox checked={isSelected} disabled={isDisabled} />
            <div className="flex flex-col items-start">
              <span className="text-body-bold font-body-bold text-default-font">
                {memberDisplayName(member)}
              </span>
              <span className="text-caption font-caption text-subtext-color">
                {isRequested ? `${roleLabel} · already asked` : roleLabel}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
